import { BitriseAppJson, TraceProfile } from '@trace/types';
import { parse, serialize } from 'cookie';
import jwtDecode from 'jwt-decode';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { NS_BITRISE_CSRF_TOKEN, NS_TRACE_APP, NS_TRACE_TOKEN } from '../../constants/LocalStorage';
import { callBitriseApp } from '../../fetch/fetch';
import useAuthentication, { AuthenticationState } from './useAuthentication';

export default function useAuthenticationTrace(): AuthenticationState<TraceProfile> {
  const location = useLocation();
  const history = useHistory();

  // Get the Trace token either from the URL or a cookie.
  const getStoredToken = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      params.delete('token');
      // Only redirect to the user's app, if there's no appId in the URL
      history.replace(`${location.pathname}?${params.toString()}`);
      return token;
    }

    return parse(document.cookie)[NS_TRACE_TOKEN];
  }, [history, location.pathname, location.search]);

  // Return the account details from the JWT
  const getData = useCallback(async (token) => {
    const data = jwtDecode(token) as { accountDetails: TraceProfile };
    // eslint-disable-next-line camelcase
    const { default_branch } = await callBitriseApp<BitriseAppJson>(
      'GET',
      `/app/${data.accountDetails.appId}.json`,
      null,
      undefined,
      {
        credentials: 'include',
        headers: {
          'x-csrf-token': parse(document.cookie)[NS_BITRISE_CSRF_TOKEN],
        },
      },
    );

    return { ...data.accountDetails, defaultBranch: default_branch };
  }, []);

  // Do nothing, as the user needs to select an App.
  const getNewToken = useCallback(async () => {
    return null;
  }, []);

  // Store both the App ID and the token as cookies.
  const setToken = useCallback(async (token: string) => {
    const { accountDetails, exp } = jwtDecode(token) as {
      accountDetails: TraceProfile;
      exp: number;
    };

    document.cookie = serialize(NS_TRACE_APP, accountDetails.appId, {
      domain: process.env.APM_DOMAIN,
      path: '/',
    });

    document.cookie = serialize(NS_TRACE_TOKEN, token, {
      expires: new Date(exp * 1000),
      domain: process.env.APM_DOMAIN,
      path: '/',
    });
  }, []);

  // Clears the Trace token
  const clearToken = () => {
    document.cookie = serialize(NS_TRACE_TOKEN, '', {
      expires: new Date(1),
      domain: process.env.APM_DOMAIN,
      path: '/',
    });
  };

  return useAuthentication({
    clearToken,
    getData,
    getNewToken,
    getStoredToken,
    setToken,
  });
}
